/**
 * The game on screen (spec 05, 08): the current level's stage, its lights, the figures, bubbles and
 * items, the frame pipeline and the side panels; the C64's scroll to the next level, the next stage
 * built below the current one and the camera moving down to it (05 §3.2); what fills the screen in
 * place of the stage (the title, the story, the ending); the player's display options, live (08 §4);
 * and the transition between screens.
 */

import { Light, Mesh, Scene, SphereGeometry, type WebGLRenderer } from 'three';
import stageGrade from '../../../content/grades/stage.json';
import type { LevelDef } from '../../levels/types';
import { gridFromRows } from '../../levels/grid';
import { effectsLook } from '../../sim/items';
import type { World } from '../../sim/types';
import type { DisplaySettings } from '../../settings';
import { createHudCanvas, drawHud, type HudColours, type HudState } from '../../ui/hud/panels';
import { type Banner, drawBanners } from '../../ui/hud/messages';
import { StageCamera } from '../camera/StageCamera';
import { levelLookFor } from '../levelLook';
import { glassBody } from '../materials/glassBubble';
import { createGradientMap, sharedUniforms } from '../materials/toon';
import { c64Lut, type Grade, Palette } from '../palette';
import { RenderPipeline } from '../pipeline/RenderPipeline';
import type { RenderSize } from '../size';
import { createStageLights, StageView } from '../stage/StageView';
import { Backdrops } from './backdrops';
import {
  DISPLAY_DEFAULTS, figurePaletteFor, fitDisplay, glassBodyFor, gradeFor, hudColoursFor, inkFor, lockFor, lutFor,
  transitionColoursFor,
} from './displayLook';
import type { Previous } from './entityViews';
import { FigureSet } from './figureSet';
import { flashPulse } from './flash';
import { transitionPhase } from './transition';

const ROWS = 25;

/** What can fill the screen in place of the stage. */
export type Backdrop = 'title' | 'story' | 'ending';

export class GameView {
  readonly scene = new Scene();
  readonly pipeline: RenderPipeline;
  private stageCamera: StageCamera;
  private readonly gradientMap = createGradientMap();
  private readonly grade = stageGrade as Grade;
  private readonly lights = createStageLights(this.grade, 32, ROWS);
  private stage: StageView | null = null;
  private next: StageView | null = null;
  private def: LevelDef;
  private nextDef: LevelDef | null = null;
  private figures: FigureSet;
  private readonly backdrops: Backdrops;
  readonly hud = createHudCanvas(document.body);
  private readonly hudCtx = this.hud.canvas.getContext('2d');
  private hudColours: HudColours;
  private ink: string;
  /** True while the figures should be hidden (the scroll between levels, the title). */
  hideFigures = false;
  private warmed = false;
  private display: DisplaySettings = { ...DISPLAY_DEFAULTS };
  /** The pixel size the current internal size is for; null until the first `setDisplay`. */
  private pixelSize: number | null = null;
  private phase = 0;
  /**
   * The internal size for a pixel size (08 §4): by default the largest that fits the drawing buffer
   * (as `chooseSize` does). A page that forces its size (`?res=`) can replace it.
   */
  sizeFor: (pixelSize: 1 | 2 | 3) => RenderSize;

  constructor(renderer: WebGLRenderer, size: RenderSize, first: LevelDef) {
    this.def = first;
    const palette = new Palette(levelLookFor(first, 'modern'));
    this.stageCamera = new StageCamera(size);
    // The C64's LUT stands in (it is made once and cheap) until setLevel builds the level's.
    this.pipeline = new RenderPipeline(renderer, c64Lut(), palette.color('ink.0'), size);
    this.sizeFor = (px) => fitDisplay(renderer, px);
    this.figures = new FigureSet(this.scene, palette, this.gradientMap, false);
    this.backdrops = new Backdrops(this.scene, this.gradientMap);
    this.scene.add(this.lights);
    this.hudColours = hudColoursFor('modern', palette);
    this.ink = inkFor('modern', palette);
    this.pipeline.setTransitionColours(transitionColoursFor('modern', palette));
    this.setLevel(first);
  }

  /** The side panels' colours in the current look (C64 colours in 1987's): the menus can draw in them too. */
  get panelColours(): HudColours {
    return this.hudColours;
  }

  /** The stage camera (a new one when the pixel size changes the internal size). */
  get camera(): StageCamera {
    return this.stageCamera;
  }

  private buildStage(def: LevelDef, y: number): StageView {
    const look = levelLookFor(def, this.display.look);
    const palette = new Palette(look);
    const stage = new StageView({ grid: gridFromRows(def.tiles), style: look.style, palette, gradientMap: this.gradientMap, seed: def.n });
    stage.group.position.y = y;
    this.scene.add(stage.group);
    return stage;
  }

  private dropStage(stage: StageView | null): null {
    if (stage) this.scene.remove(stage.group);
    stage?.dispose();
    return null;
  }

  /** Show `def` as the level in play, with nothing else on screen. */
  setLevel(def: LevelDef): void {
    this.stage = this.dropStage(this.stage);
    this.next = this.dropStage(this.next);
    this.nextDef = null;
    this.def = def;
    this.stage = this.buildStage(def, 0);
    this.stage.group.visible = this.backdrops.showing === null;
    this.applyPalette();
    this.focus(0);
  }

  /** Build the next level below this one, ready to scroll to (the LUT covers both). */
  beginScroll(def: LevelDef): void {
    this.next = this.dropStage(this.next);
    this.nextDef = def;
    this.next = this.buildStage(def, -ROWS);
    this.applyPalette();
  }

  /** How far down the scroll is, 0..1. */
  scroll(p: number): void {
    this.focus(-p * ROWS);
  }

  /** The scroll is over: the next level becomes the level in play, back at the origin. */
  endScroll(def: LevelDef): void {
    this.setLevel(def);
  }

  /** The level's LUT (both levels' during a scroll; the old one freed) and its grade and fog. */
  private applyPalette(): void {
    const look = this.display.look;
    const lut = lutFor(look, this.def, this.nextDef);
    const old = this.pipeline.setLut(lut);
    if (old !== lut && old !== c64Lut()) old.dispose();
    this.pipeline.setGrade(gradeFor(look, this.grade), levelLookFor(this.def, look).fog);
  }

  private focus(y: number): void {
    this.stageCamera.focus.set(0, y, 0);
    this.stageCamera.update();
    this.lights.position.y = y;
  }

  /** Draw a frame: the world `acc` of the way to its next sim frame, the panels, any banners. */
  render(
    world: World | null, prev: Previous, acc: number, t: number, hud: HudState, banners: readonly Banner[],
    overlay?: (ctx: CanvasRenderingContext2D) => void,
  ): void {
    sharedUniforms.uTime.value = t;
    if (!this.warmed) this.warmUp();
    this.backdrops.frame(t);
    const shown = world !== null && !this.hideFigures;
    if (world && shown) this.figures.sync(world, prev, acc, t);
    const fx = world && shown ? effectsLook(world) : null;
    this.clockFlash(fx !== null && (fx.clock || fx.flash), fx?.flash === true ? 10 : 4, t);
    this.setFiguresVisible(shown);
    this.pipeline.render(this.scene, this.stageCamera);
    if (this.hudCtx) {
      this.hud.place(this.pipeline.viewRect);
      drawHud(this.hudCtx, hud, this.hudColours);
      drawBanners(this.hudCtx, banners, this.ink);
      overlay?.(this.hudCtx);
    }
  }

  /**
   * Compile every material that can appear in play before the first frame, the aids' included. three
   * compiles a shader the first time something using it is drawn, so the first bubble, trapped monster
   * or fruit would otherwise stall a frame or several as it appeared - and the sim would catch up in a
   * lurch. Done again after the look changes (the figures are made anew; their programs are cached).
   */
  private warmUp(): void {
    this.warmed = true;
    const geometry = new SphereGeometry(0.01, 4, 3);
    const probes = this.figures.materials().map((m) => {
      const mesh = new Mesh(geometry, m);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      return mesh;
    });
    this.scene.add(...probes);
    this.pipeline.render(this.scene, this.stageCamera);
    this.scene.remove(...probes);
    geometry.dispose();
  }

  /**
   * The clock's freeze (02 §7.5): the C64 flashes the background colours; a strike starting, a blast,
   * an umbrella or a door flash the border while play pauses. Here the stage's lights pulse while
   * either lasts, faster for the pauses - capped at 3 Hz and softened with reduce flashing (flash.ts).
   */
  private clockFlash(on: boolean, hz: number, t: number): void {
    const k = on ? 1 + flashPulse(t, hz, 0.45, this.display.reduceFlashing) : 1;
    this.lights.traverse((o) => {
      if (!(o instanceof Light)) return;
      const base = o.userData['base'];
      if (typeof base !== 'number') o.userData['base'] = o.intensity;
      o.intensity = (typeof base === 'number' ? base : o.intensity) * k;
    });
  }

  /**
   * What fills the screen in place of the stage: the title or the story (08 §1), the ending (02 §9.4,
   * a starfield, the big boss pacing the bottom); null is the stage again.
   */
  showBackdrop(kind: Backdrop | null): void {
    this.backdrops.show(kind, this.figures, figurePaletteFor(this.def, this.display.look));
    if (this.stage) this.stage.group.visible = kind === null;
    this.focus(0);
  }

  /** The ending on or off (kept for the session; `showBackdrop` is the general form). */
  showEnding(on: boolean): void {
    this.showBackdrop(on ? 'ending' : null);
  }

  /**
   * The player's display options (08 §4), applied at once: the look (the 1987 look rebuilds the stage and
   * the figures in the C64's colours), CRT, palette lock, pixel size (a new internal size: new targets and
   * camera, the old freed), the colour aid and reduce flashing. The first call takes the size the view was
   * made with to be for its pixel size; later changes of pixel size resize through `sizeFor`.
   */
  setDisplay(d: DisplaySettings): void {
    const before = this.display;
    this.display = { ...d };
    const s = this.pipeline.settings;
    s.crt = d.crt;
    Object.assign(s, lockFor(d));
    if (d.look !== before.look) this.applyLook();
    this.figures.setOptions({ colourAid: d.colourAid, reduceFlashing: d.reduceFlashing });
    if (this.pixelSize !== null && this.pixelSize !== d.pixelSize) this.resize(this.sizeFor(d.pixelSize));
    this.pixelSize = d.pixelSize;
  }

  /** The look changed: the stage, the figures, whatever backdrop is up, the LUT and the colours drawn outside it. */
  private applyLook(): void {
    const look = this.display.look;
    glassBody.value = glassBodyFor(look);
    const figures = figurePaletteFor(this.def, look);
    this.figures.dispose();
    this.figures = new FigureSet(this.scene, figures, this.gradientMap, look === '1987');
    this.backdrops.rebuild(this.figures, figures);
    const y = this.next?.group.position.y;
    this.stage = this.dropStage(this.stage);
    this.stage = this.buildStage(this.def, 0);
    this.stage.group.visible = this.backdrops.showing === null;
    if (this.next && this.nextDef && y !== undefined) {
      this.next = this.dropStage(this.next);
      this.next = this.buildStage(this.nextDef, y);
    }
    this.applyPalette();
    this.hudColours = hudColoursFor(look, figures);
    this.ink = inkFor(look, figures);
    this.pipeline.setTransitionColours(transitionColoursFor(look, figures));
    this.warmed = false;
  }

  /** A new internal size: the pipeline's targets and a camera to match, looking where the old one did. */
  private resize(size: RenderSize): void {
    const y = this.stageCamera.focus.y;
    this.pipeline.resize(size);
    this.stageCamera = new StageCamera(size);
    this.focus(y);
  }

  /** A transition between screens: 0 clear, 1 fully covered (bubbles rise in, then burst away). */
  setTransition(p: number): void {
    this.phase = transitionPhase(p, this.phase);
    this.pipeline.setTransition(this.phase);
  }

  private setFiguresVisible(on: boolean): void {
    for (const o of this.scene.children) {
      if (o === this.lights || o === this.stage?.group || o === this.next?.group || this.backdrops.owns(o)) continue;
      if (!on) o.visible = false;
    }
  }

  /** Forget every monster, bubble and item (a new game, a new level); the dragons stay. */
  resetFigures(): void {
    this.figures.clear();
  }
}
